import { useTheme } from '@emotion/react';
import { Box, Stack, Typography, IconButton, Button, TextField, Card, Grid, Chip, Divider } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import Tooltip from "@mui/material/Tooltip";
import {
  ApiResponse as ApiResponseComponent,
  CustomDivider,
  InputField,
  UploadImage,
  DigilockerAuth
} from "../../components";
import { useDispatch } from "react-redux";
import { useResponseContext } from "../../components/ApiResponse/ApiResponse";
import { Download } from "../../assets";
import CurlRequestGenerator from "../dashboard/utils";
import { onscreenTestHeaders } from "../../services/constants";
import { createDigiLockerUrl } from "../../services/onscreenApiService";
import { collectiveApiRequest } from "../../services";
import { totalCreditsAndRemaining } from "../../services/creditsService";
import { remainingCredits } from "../../store/reducers/auth";
import { getApiDocsData } from "../../services/apiDocsService";
import TextSnippetOutlinedIcon from "@mui/icons-material/TextSnippetOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import { PdfUploadForm } from '../../components/upload-input/PdfUploadForm';
import { TechnicalDocOcr } from '../../components/upload-input/TechnicalDocOcr';

export function Combinator({
  queryParamsFlag = false,
  apiInput = [],
  apiUrl = "",
  apiResponse = "json",
  file = null,
  setFile = () => {},
  jsonData = null,
  setJsonData = () => {},
  image = { uploaded: null, masked: null },
  setImage = () => {},
  status = null,
  setStatus = () => {},
  linkedApis = [],
  selectedAPI = null,
  selectedCategory = null,
  apiCategoryAndList = {},
  api = null,
}) {
  const theme = useTheme();
  const [loader, setLoader] = useState(false);
  const [downloadedUrl, setDownloadedUrl] = useState("");
  const [textCopied, setTextCopied] = useState(false);
  const [errorObject, setErrorObject] = useState(null);
  const [curlUrl, setCurlUrl] = useState("");
  const [curlMethod, setCurlMethod] = useState("");
  const [curlBody, setCurlBody] = useState("");
  const [requestId, setRequestId] = useState("");
  const [inputValues, setInputValues] = useState({});
  const [verificationResponse, setVerificationResponse] = useState(null);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [verificationLoader, setVerificationLoader] = useState(false);
  const [docLoading, setDocLoading] = useState(false);

  const { transactionId, setTransactionId } = useResponseContext();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentApiInput = apiInput[0] || {};
  const { text = [], upload = [], queryParam = false } = currentApiInput;

  const getSelectedApiInfo = (apiId) => {
    const categories = Object.values(apiCategoryAndList);
    for (const category of categories) {
      // Convert both to string for comparison to handle number/string mismatch from URL params
      const foundApi = category.apis.find((api) => String(api.id) === String(apiId));
      if (foundApi) {
        return foundApi;
      }
    }
    return null;
  };

  const handleDocumentationClick = async () => {
    const selectedApi = getSelectedApiInfo(selectedAPI);
    if (selectedApi) {
      try {
        setDocLoading(true);
        // Call the /api-doc endpoint to fetch documentation data
        await getApiDocsData();
        // Navigate to api-document page with state
        navigate("/api-document", {
          state: {
            selectedSubCategory: selectedApi.name,
            selectedCategory: selectedCategory,
            showBackButton: true,
          },
        });
      } catch (error) {
        console.error("Failed to fetch API documentation:", error);
        // Still navigate even if API call fails, as data might be cached
        navigate("/api-document", {
          state: {
            selectedSubCategory: selectedApi.name,
            selectedCategory: selectedCategory,
            showBackButton: true,
          },
        });
      } finally {
        setDocLoading(false);
      }
    }
  };

  const hostname = window.location.hostname;

  const stageHostnames = ["localhost", "finhub-stage.habilelabs.io"];
  const apiMap = {
    "customer-test.finhub.habilelabs.io": "https://api-tejas-test.finhub.habilelabs.io",
    "customer.finhub.habilelabs.io": "https://api-tejas.finhub.habilelabs.io"
  };

  const onscreenApiUrl = stageHostnames.includes(hostname)
    ? "https://api-tejas-stage.finhub.habilelabs.io"
    : apiMap[hostname] || "https://api-gate.finhub.habilelabs.io";

  // Extract transaction ID from response and auto-fill DigiLocker Aadhaar
  useEffect(() => {
    const id =
      jsonData?.tran_id ||
      jsonData?.data?.tran_id ||
      jsonData?.transaction_id ||
      jsonData?.transactionId;

    if (id) {
      setTransactionId(id);
      setInputValues((prev) => ({ ...prev, tran_id: id, transId: id }));
    }

    // Auto-fill Aadhaar number for DigiLocker APIs
    const digilockerAadhaar = sessionStorage.getItem('digilocker_aadhaar');
    if (apiUrl?.includes('/v1/digi') && digilockerAadhaar && !inputValues.requestId) {
      setInputValues(prev => ({ ...prev, requestId: digilockerAadhaar }));
    }
  }, [jsonData, setTransactionId, apiUrl, inputValues.requestId]);

  const handleDownload = () => {
    if (apiResponse === "image" && downloadedUrl) {
      window.open(downloadedUrl, "_blank");
    }
  };
  const handleDownloadurl = (url) => {
    if (!url) return;

    const link = document.createElement("a");
    link.href = url;
    link.download = "document.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCopyText = () => {
    const activeTab = document
      .querySelector(".MuiTab-root.Mui-selected")
      ?.textContent?.toLowerCase();
    if (activeTab === "data preview") {
      const tableElement = document.querySelector(".MuiTable-root");
      if (tableElement) {
        const range = document.createRange();
        range.selectNode(tableElement);
        window.getSelection().removeAllRanges();
        window.getSelection().addRange(range);

        try {
          document.execCommand("copy");
          window.getSelection().removeAllRanges();
          setTextCopied(true);
          setTimeout(() => setTextCopied(false), 2000);
        } catch (err) {
          console.error("Failed to copy table content:", err);
        }
      }
    } else if (activeTab === "json") {
      const textToCopy = JSON.stringify(jsonData, null, 2);
      navigator.clipboard
        .writeText(textToCopy)
        .then(() => {
          setTextCopied(true);
          setTimeout(() => setTextCopied(false), 2000);
        })
        .catch((err) => {
          console.error("Failed to copy text: ", err);
        });
    }
  };
  const handleFileChange = (newFile) => {
    setFile(newFile);
  };
  useEffect(() => {
    setImage((prev) => ({ ...prev, uploaded: null, masked: null }));
    setJsonData(null);
    setTextCopied(false);
    setInputValues({});
    setTransactionId(null);
    setVerificationResponse(null);
    setVerificationStatus(null);
  }, [apiInput, setImage, setJsonData, setTransactionId]);

  const handleCreateUrl = async () => {
    try {
      const response = await createDigiLockerUrl();
      if (response?.status === 200) {
        setRequestId(response.data.result.requestId);
        window.open(response.data.result.url);
      }
    } catch (error) {
      setErrorObject(error);
    }
  };

  const handleInputChange = (fieldName, value) => {
    if (fieldName === "otp") {
      const numericValue = value.replace(/[^0-9]/g, "");
      if (numericValue.length <= 6) {
        setInputValues((prev) => ({ ...prev, [fieldName]: numericValue }));
      }
    } else {
      setInputValues((prev) => ({ ...prev, [fieldName]: value }));
    }
  };

  const handleVerifySubmit = async (api) => {
    try {
      setVerificationLoader(true);
      setLoader(true);
      setVerificationResponse(null);
      setVerificationStatus(null);

      const payload = {};
      api.input?.forEach((inputField) => {
        payload[inputField.value] =
          inputValues[inputField.value] ||
          jsonData?.[inputField.value] ||
          jsonData?.data?.[inputField.value];
      });

      const response = await collectiveApiRequest(payload, api.url);

      setVerificationResponse(response.data);
      setVerificationStatus(response.status);
      setJsonData(response.data); // Overwrite the main response
      setTimeout(async () => {
        const remainingCredit = await totalCreditsAndRemaining();
        if (remainingCredit?.status === 200) {
          dispatch(
            remainingCredits({
              credits: remainingCredit?.data?.credit?.remainingCredit,
            })
          );
          setLoader(false);
        }
      }, 2000);
      setStatus(response.status); // Update the status
      setInputValues({});
    } catch (error) {
      const errorData = error.response?.data || { error: error.message };
      setVerificationResponse(errorData);
      setVerificationStatus(error.response?.status || 500);
      setErrorObject(error);
      setLoader(false);
      setJsonData(errorData); // Overwrite the main response with error
      setStatus(error.response?.status || 500);
      if (linkedApis.length > 0 && text.length > 0) {
        setInputValues((prev) => {
          const { otp, ...rest } = prev;
          return rest;
        });
      }
    } finally {
      setVerificationLoader(false);
    }
  };

  const isVerifyButtonEnabled = (api) => {
    return inputValues.otp?.length === 6;
  };

  return (
    <Box p={2}>
      <Box
        mb={4}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          flexDirection: {
            xs: "column",
            sm: "row",
          },
          padding: "20px",
          borderRadius: "12px",
          border: "1px solid #E1E7EF",
          backgroundColor: "#FFF",
          boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.05)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Chip
             label={queryParam ? "GET" : "POST"}
            color="success"
            sx={{ fontSize: "16px" }}
          />
          <Typography
            variant="body2"
            sx={{ fontSize: "18px", fontWeight: "400", color: "#020817" }}
          >
            {apiUrl ? `${apiUrl}` : "API Endpoint"}
          </Typography>
        </Box>
        
        <Box
          onClick={handleDocumentationClick}
          sx={{
            fontSize: "12px",
            fontWeight: "400",
            borderRadius: "10px",
            border: "1px solid #E1E7EF",
            backgroundColor: "#FFF",
            boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.05)",
            padding: "3px 10px",
            cursor: docLoading ? "not-allowed" : "pointer",
            marginTop: "10px",
            opacity: docLoading ? 0.6 : 1,
            "&:hover": {
              backgroundColor: docLoading ? "#FFF" : "#f5f5f5",
            },
          }}
        >
          <Typography
            sx={{ display: "flex", alignItems: "center", gap: "5px" }}
          >
            <TextSnippetOutlinedIcon /> {docLoading ? "Loading..." : "Documentation"} <OpenInNewOutlinedIcon />
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={4}>
        {/* Input Card */}
        <Grid item xs={12} md={6}>
          <Card
            elevation={2}
            sx={{
              height: "100%",
              borderRadius: "12px",
              border: "1px solid #E1E7EF",
              backgroundColor: "#FFF",
              boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
            }}
          >
            <Box
              sx={{
                p: 3,
                borderBottom: "1px solid",
                borderColor: "divider",
                minHeight: "110px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                }}
              >
                <Typography
                  variant="h5"
                  sx={{ fontSize: "18px", color: "#020817", fontWeight: "700" }}
                >
                  Input
                </Typography>
                 <Box sx={{ ml: "auto" }}>
                  <CurlRequestGenerator
                    method={curlMethod}
                    url={`${onscreenApiUrl}${curlUrl}`}
                    headers={onscreenTestHeaders()}
                    body={curlBody}
                    apiUrl={apiUrl}
                    queryParamsFlag={queryParamsFlag}
                  />
                  </Box>  
              </Box>
              <Typography
                variant="body1"
                sx={{
                  fontSize: "16px",
                  fontWeight: "400",
                }}
              >
                Configure your API request parameters
              </Typography>
            </Box>

            <Box
              sx={{
                p: { xs: 1, sm: 3 },
              }}
            >
               {upload?.length > 0 && text?.length > 0 && apiUrl?.includes('/v1/aadhaar-esign') && (
              <PdfUploadForm
                apiUrl={apiUrl}
                setJsonData={setJsonData}
                setStatus={setStatus}
                setloader={setLoader}
                setCurlMethod={setCurlMethod}
                setCurlUrl={setCurlUrl}
                setCurlBody={setCurlBody}
              />
              )}

            {upload?.length > 0 && text?.length > 0 && apiUrl?.includes('/v1/technical-doc-ocr') &&(
              <TechnicalDocOcr
                apiUrl={apiUrl}
                setJsonData={setJsonData}
                setStatus={setStatus}
                setloader={setLoader}
                setCurlMethod={setCurlMethod}
                setCurlUrl={setCurlUrl}
                setCurlBody={setCurlBody}
              />
              )}

              {/* Upload Section */}
              {upload?.length > 0 && text?.length < 1 && (
                <UploadImage
                  setStatus={setStatus}
                  apiResponse={apiResponse}
                  upload={upload}
                  setImage={setImage}
                  setloader={setLoader}
                  setJsonData={setJsonData}
                  setDownloadedUrl={setDownloadedUrl}
                  image={image}
                  apiUrl={apiUrl}
                  errorObject={errorObject}
                  setErrorObject={setErrorObject}
                  file={file}
                  setFile={setFile}
                  setCurlMethod={setCurlMethod}
                  setCurlUrl={setCurlUrl}
                  setCurlBody={setCurlBody}
                  onFileChange={handleFileChange}
                  text={text}
                  buttonText="Try API"
                />
              )}

              {/* Digilocker Button */}
              {apiUrl?.includes('/v1/digi') ? (
                <DigilockerAuth
                    onAuthSuccess={(data) => {
                      if (data) {
                        setJsonData(data);
                        setStatus(200);
                      } else {
                        // Clear data when null is passed (new number entered)
                        setJsonData(null);
                        setStatus(null);
                      }
                    }}
                    onAuthError={(error) => {
                      setErrorObject(error);
                      const statusCode = error.response?.status || 500;
                      const errorData = error.response?.data || { error: error.message || 'DigiLocker error' };
                      setStatus(statusCode);
                      setJsonData(errorData);
                    }}
                    disabled={loader}
                    setCurlMethod={setCurlMethod}
                    setCurlUrl={setCurlUrl}
                    setCurlBody={setCurlBody}
                    apiUrl={apiUrl}
                  />
              ) : (
              text?.length > 0 && upload?.length < 1 && (
                <Box mt={2} sx={{ width: "100%" }}>
                  <InputField
                    sx={{ width: "100%" }}
                    queryParamsFlag={queryParamsFlag}
                    setStatus={setStatus}
                    text={text}
                    setImage={setImage}
                    setloader={setLoader}
                    setJsonData={setJsonData}
                    apiUrl={apiUrl}
                    errorObject={errorObject}
                    setErrorObject={setErrorObject}
                    setCurlUrl={setCurlUrl}
                    setCurlMethod={setCurlMethod}
                    setCurlBody={setCurlBody}
                    requestId={requestId}
                    inputValues={inputValues}
                    onInputChange={handleInputChange}
                    buttonText="Try API"
                    file={file}
                    setVerificationResponse={setVerificationResponse}
                    linkedApis={linkedApis}
                    jsonData={jsonData}
                  />
                </Box>
              )
              )}
              {/* Linked APIs Section */}
              {linkedApis?.length > 0 && jsonData && (
                <Box mt={4}>
                  <Divider sx={{ my: 2 }}>Verification</Divider>
                  <Box
                    sx={{
                      backgroundColor: theme.palette.grey.lighten5,
                      borderRadius: 1,
                      p: 2,
                      borderLeft: `4px solid ${theme.palette.primary.main}`,
                    }}
                  >
                    {linkedApis.map((api) => (
                      <Box key={api.id} sx={{ mb: 2 }}>
                        {api.input?.map((inputField) => (
                          <Box key={inputField.id} sx={{ mt: 1, mb: 2 }}>
                            <Typography variant="body1" fontWeight="500" mb={1}>
                              {inputField.label}
                              <span style={{ color: theme.palette.error.main }}>
                                *
                              </span>
                            </Typography>
                            <TextField
                              fullWidth
                              variant="outlined"
                              label={`Enter ${inputField.label}`}
                              value={inputValues[inputField.value] || ""}
                              onChange={(e) =>
                                handleInputChange(
                                  inputField.value,
                                  e.target.value
                                )
                              }
                              sx={{ mb: 2, fontSize: "1.1rem" }}
                              size="medium"
                              inputProps={{
                                maxLength:
                                  inputField.value === "otp" ? 6 : undefined,
                                inputMode:
                                  inputField.value === "otp"
                                    ? "numeric"
                                    : "text",
                                pattern:
                                  inputField.value === "otp"
                                    ? "\\d*"
                                    : undefined,
                                style: { fontSize: "1.1rem" },
                              }}
                            />
                          </Box>
                        ))}

                        <Button
                          variant="contained"
                          onClick={() => handleVerifySubmit(api)}
                          disabled={
                            !isVerifyButtonEnabled(api) ||
                            verificationLoader ||
                            verificationResponse?.status === "SUCCESS" ||
                            verificationResponse?.data?.uid
                          }
                          size="large"
                          fullWidth
                        >
                          {verificationLoader
                            ? "Verifying..."
                            : `Verify ${api.name}`}
                        </Button>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Card>
        </Grid>
        {/* Output Card */}
        <Grid item xs={12} md={6}>
          <Card
            elevation={2}
            sx={{
              height: "100%",
              borderRadius: "12px",
              border: "1px solid #E1E7EF",
              backgroundColor: "#FFF",
              boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
            }}
          >
            <Box
              sx={{
                p: 3,
                borderBottom: "1px solid",
                borderColor: "divider",
                minHeight: "110px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontSize: "18px",
                      fontWeight: "700",
                      color: "#020817",
                    }}
                  >
                    Output
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  {apiResponse === "json" ? (
                    <Tooltip
                      title="Copy"
                      componentsProps={{
                        tooltip: {
                          sx: {
                            fontSize: "12px",
                          },
                        },
                      }}
                    >
                      <IconButton
                        onClick={handleCopyText}
                        style={{
                          cursor: "pointer",
                          gap: 2,
                          color: "#020817",
                        }}
                      >
                        <ContentCopyIcon sx={{ fontSize: "2rem" }} />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Tooltip
                      title="Download"
                      componentsProps={{
                        tooltip: {
                          sx: {
                            fontSize: "12px",
                          },
                        },
                      }}
                    >
                      <IconButton
                        disableRipple
                        onClick={handleDownload}
                        style={{
                          cursor: "pointer",
                          fontSize: "1.5rem",
                          gap: 2,
                          color: "#020817",
                        }}
                      >
                        <img src={Download} alt="Download" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {jsonData?.downloadUrl && (
                    <Tooltip
                      title="Download CSV "
                      componentsProps={{
                        tooltip: {
                          sx: {
                            fontSize: "12px",
                          },
                        },
                      }}
                    >
                      <IconButton
                        disableRipple
                        onClick={() => handleDownloadurl(jsonData.downloadUrl)}
                        style={{
                          cursor: "pointer",
                          fontSize: "2rem",
                          gap: 2,
                          color: "#020817",
                        }}
                      >
                        <FileDownloadOutlinedIcon sx={{ fontSize: "2rem" }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                p: { xs: 1, sm: 3 },
              }}
            >
              <ApiResponseComponent
                loader={loader || verificationLoader}
                response={{
                  image,
                  text: verificationResponse || jsonData,
                  status: verificationStatus || status,
                }}
                errorObject={errorObject}
                hideStatus={true}
              />
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}